/**
 * PermisHauteurForm.jsx — Permis de travail en hauteur (Lafarge)
 * Reproduit les blocs du template :
 *   - Hiérarchie des contrôles (A/B/C/D)
 *   - Identité opération (nb personnes harnais, hauteur de chute, mode opératoire)
 *   - Système 1 — Retenue de chute (longueur, ancrage)
 *   - Système 2 — Stop chute à enrouleur avec absorbeur
 *   - Système 3 — Liaison(s) avec absorbeur + tirant d'air
 *   - Plan de secours (référence, kit, nacelle, pompiers)
 *   - Signataires
 */
import { Ruler } from 'lucide-react';
import FormationPicker from '@/components/FormationPicker';

function Checkbox({ checked, onChange, label, testid, className = '' }) {
  return (
    <label className={`flex items-start gap-2 cursor-pointer text-xs ${className}`} data-testid={testid}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-[#DA291C] flex-shrink-0" />
      <span className="leading-snug">{label}</span>
    </label>
  );
}

function YesNo({ value, onChange, testid }) {
  return (
    <div className="inline-flex gap-1" data-testid={testid}>
      {['oui', 'non'].map((v) => (
        <button key={v} type="button" onClick={() => onChange(value === v ? '' : v)}
          className={`px-2 py-0.5 text-[11px] font-bold uppercase border ${
            value === v ? (v === 'oui' ? 'bg-green-600 text-white border-green-600' : 'bg-stone-700 text-white border-stone-700') : 'bg-white border-stone-300 text-stone-600'
          }`}>{v}</button>
      ))}
    </div>
  );
}

function NumInput({ value, onChange, testid, suffix, placeholder, width = 'w-20' }) {
  return (
    <div className="inline-flex items-center gap-1">
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} placeholder={placeholder}
        className={`${width} px-2 py-1 text-xs border border-stone-300 focus:border-[#09090B] outline-none font-mono`} />
      {suffix && <span className="text-[10px] text-stone-500 uppercase">{suffix}</span>}
    </div>
  );
}

function SectionTitle({ children, accent = 'bg-[#09090B]' }) {
  return (
    <div className={`${accent} text-white text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2`}>
      {children}
    </div>
  );
}

export default function PermisHauteurForm({ extra, setExtra, dateDebut, dateFin, titre, lieu, entreprise, compact = false, siteCode = '' }) {
  return (
    <div className="space-y-4 border-2 border-blue-600 bg-white" data-testid="permis-hauteur-form">
      <div className="bg-blue-600 text-white px-4 py-3 flex items-center gap-3">
        <Ruler className="w-6 h-6" strokeWidth={2.5} />
        <div>
          <div className="text-2xl font-display font-black tracking-tighter">PERMIS DE TRAVAIL EN HAUTEUR</div>
          <div className="text-[10px] uppercase tracking-wider opacity-90">
            Hauteur de travail ≥ 1,8 m — Avec risque de chute
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        {/* Hiérarchie des contrôles */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Hiérarchie des contrôles — Principes de prévention</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-stone-50/50">
            <div className="flex items-center justify-between gap-3">
              <span className="flex-1"><b>A.</b> Le risque peut-il être immédiatement éliminé (travail ramené au sol) ?</span>
              <YesNo value={extra.ctrl_a_sol} onChange={(v) => setExtra('ctrl_a_sol', v)} testid="th-ctrl-a" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="flex-1"><b>B.</b> Peut-on installer immédiatement une plateforme, PIR ?</span>
              <YesNo value={extra.ctrl_b_pir} onChange={(v) => setExtra('ctrl_b_pir', v)} testid="th-ctrl-b" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="flex-1"><b>C.</b> Peut-on faire monter un échafaudage FIXE ?</span>
              <YesNo value={extra.ctrl_c_echafaudage} onChange={(v) => setExtra('ctrl_c_echafaudage', v)} testid="th-ctrl-c" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="flex-1"><b>D.</b> Peut-on utiliser une nacelle ?</span>
              <YesNo value={extra.ctrl_d_nacelle} onChange={(v) => setExtra('ctrl_d_nacelle', v)} testid="th-ctrl-d" />
            </div>
            <div className="text-[10px] italic text-stone-600 pt-1 border-t border-stone-200">
              Si OUI à l'une de ces 4 questions, mettre la solution en œuvre : le permis n'est pas nécessaire.
            </div>
          </div>
        </div>

        {/* Identité opération — En mode compact, "Travail fait par" hérité (vide entreprise = Lafarge) */}
        <div>
          <SectionTitle>Identité de l'opération</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-3 text-xs">
            {!compact && (
            <div>Travail fait par :
              <select className="ml-2 px-2 py-1 border border-stone-300 text-xs" value={extra.travail_par || ''}
                onChange={(e) => setExtra('travail_par', e.target.value)} data-testid="th-travail-par">
                <option value="">—</option>
                <option value="Lafarge">Salariés Lafarge</option>
                <option value="EE">Entreprise extérieure</option>
              </select>
            </div>
            )}
            <div>Nb personnes portant le harnais : <NumInput value={extra.nb_personnes_harnais} onChange={(v) => setExtra('nb_personnes_harnais', v)} testid="th-nb-harnais" /></div>
            <div>Hauteur de chute : <NumInput value={extra.hauteur_chute} onChange={(v) => setExtra('hauteur_chute', v)} testid="th-hauteur-chute" suffix="m" /></div>
            <div>Mode opératoire existant :
              <YesNo value={extra.mode_op_existant} onChange={(v) => setExtra('mode_op_existant', v)} testid="th-mode-op" />
              <input type="text" value={extra.mode_op_ref || ''} onChange={(e) => setExtra('mode_op_ref', e.target.value)}
                data-testid="th-mode-op-ref" placeholder="Réf. document" className="ml-2 px-2 py-1 text-xs border border-stone-300" />
            </div>
          </div>
        </div>

        {/* Système 1 — Retenue de chute */}
        <div>
          <SectionTitle accent="bg-emerald-700">Système 1 — Retenue de chute (Sans risque de chute)</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-emerald-50/30">
            <Checkbox checked={extra.sys1_active} onChange={(v) => setExtra('sys1_active', v)} label="Système utilisé sur ce chantier" testid="th-sys1-active" />
            <div>Longueur liaison max : <NumInput value={extra.sys1_longueur} onChange={(v) => setExtra('sys1_longueur', v)} testid="th-sys1-longueur" suffix="m" /></div>
            <div>Type d'ancrage :
              <Checkbox checked={extra.sys1_ancrage_fixe} onChange={(v) => setExtra('sys1_ancrage_fixe', v)} label="Fixe" testid="th-sys1-fixe" className="inline-flex ml-3" />
              <Checkbox checked={extra.sys1_ancrage_temporaire} onChange={(v) => setExtra('sys1_ancrage_temporaire', v)} label="Temporaire" testid="th-sys1-temp" className="inline-flex ml-3" />
            </div>
            <div>Description :
              <input type="text" value={extra.sys1_description || ''} onChange={(e) => setExtra('sys1_description', e.target.value)}
                data-testid="th-sys1-desc" className="ml-2 px-2 py-1 text-xs border border-stone-300 w-2/3" />
            </div>
          </div>
        </div>

        {/* Système 2 — Stop chute */}
        <div>
          <SectionTitle accent="bg-amber-700">Système 2 — Stop chute à enrouleur avec absorbeur (Facteur de chute = 0)</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-amber-50/30">
            <Checkbox checked={extra.sys2_active} onChange={(v) => setExtra('sys2_active', v)} label="Système utilisé" testid="th-sys2-active" />
            <Checkbox checked={extra.sys2_ancrage_fixe} onChange={(v) => setExtra('sys2_ancrage_fixe', v)} label="Ancrage fixe (anneau certifié / structure IPN-UPN)" testid="th-sys2-fixe" />
            <Checkbox checked={extra.sys2_ligne_vie} onChange={(v) => setExtra('sys2_ligne_vie', v)} label="Ligne de vie (obligatoire si déplacement)" testid="th-sys2-lv" />
            <Checkbox checked={extra.sys2_ancrage_temporaire} onChange={(v) => setExtra('sys2_ancrage_temporaire', v)} label="Ancrage temporaire (sangle / pince poutre)" testid="th-sys2-temp" />
            <Checkbox checked={extra.sys2_double_stop} onChange={(v) => setExtra('sys2_double_stop', v)} label="Ancrage discontinu : Double stop chute avec enrouleur obligatoire" testid="th-sys2-double" />
          </div>
        </div>

        {/* Système 3 — Tirant d'air */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Système 3 — Liaison(s) avec absorbeur (0 &lt; FC ≤ 2)</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-red-50/30">
            <Checkbox checked={extra.sys3_active} onChange={(v) => setExtra('sys3_active', v)} label="Système utilisé" testid="th-sys3-active" />
            <div>Tirant d'air ³ : <NumInput value={extra.tirant_air} onChange={(v) => setExtra('tirant_air', v)} testid="th-tirant-air" suffix="m" /></div>
            <Checkbox checked={extra.tirant_air_libre} onChange={(v) => setExtra('tirant_air_libre', v)} label="Le tirant d'air est libre d'obstacle (sinon retirer les obstacles ou réhausser l'ancrage)" testid="th-tirant-libre" />
          </div>
        </div>

        {/* Supervision / Balisage */}
        <div>
          <SectionTitle>Supervision & Balisage</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-stone-50/50">
            <Checkbox checked={extra.supervision} onChange={(v) => setExtra('supervision', v)} label="SUPERVISION assurée dès qu'il y a port du harnais" testid="th-supervision" />
            <Checkbox checked={extra.balisage_zone} onChange={(v) => setExtra('balisage_zone', v)} label="BALISAGE mis en place sous la zone d'intervention" testid="th-balisage" />
            <Checkbox checked={extra.ouverture_plancher} onChange={(v) => setExtra('ouverture_plancher', v)} label="Si ouverture de plancher : sécurisation obligatoire de la zone" testid="th-ouv-plancher" />
          </div>
        </div>

        {/* Plan de secours */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Plan de secours</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-red-50/30">
            <div>Plan de sauvetage de la tâche (référence) :
              <input type="text" value={extra.plan_sauvetage_ref || ''} onChange={(e) => setExtra('plan_sauvetage_ref', e.target.value)}
                data-testid="th-plan-ref" className="ml-2 px-2 py-1 text-xs border border-stone-300 w-1/2" placeholder="Réf. document" />
            </div>
            <div className="flex items-center gap-2">
              <span>Kit de sauvetage sur chantier :</span>
              <YesNo value={extra.kit_sauvetage} onChange={(v) => setExtra('kit_sauvetage', v)} testid="th-kit" />
              <input type="text" value={extra.kit_type || ''} onChange={(e) => setExtra('kit_type', e.target.value)}
                data-testid="th-kit-type" placeholder="Type" className="ml-2 px-2 py-1 text-xs border border-stone-300" />
            </div>
            <div className="flex items-center gap-2">
              <span>Nacelle de secours nécessaire :</span>
              <YesNo value={extra.nacelle_secours} onChange={(v) => setExtra('nacelle_secours', v)} testid="th-nac-sec" />
              <input type="text" value={extra.nacelle_position || ''} onChange={(e) => setExtra('nacelle_position', e.target.value)}
                data-testid="th-nac-pos" placeholder="Positionnement" className="ml-2 px-2 py-1 text-xs border border-stone-300" />
            </div>
            <div className="flex items-center gap-2">
              <span>Pompiers / Grimp en préalerte :</span>
              <YesNo value={extra.pompiers_prealerte} onChange={(v) => setExtra('pompiers_prealerte', v)} testid="th-pompiers" />
              <span className="ml-2 font-mono text-[11px] text-[#DA291C]">N° secours : 02 43 66 4442</span>
            </div>
          </div>
        </div>

        {/* Signataires */}
        <div>
          <SectionTitle>Signataires</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Chargé de travaux</div>
              <input type="text" value={extra.charge_travaux || ''} onChange={(e) => setExtra('charge_travaux', e.target.value)}
                data-testid="th-charge-travaux" className="w-full px-2 py-1.5 border border-stone-300" placeholder="Nom, Prénom, fonction" />
            </div>
            <div>
              <FormationPicker
                module="wah"
                site={siteCode}
                value={extra.donneur_ordre || ''}
                onChange={(name) => setExtra('donneur_ordre', name)}
                testid="th-donneur"
                placeholder="Donneur d'ordre Lafarge (validateur WAH)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
